import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, Sparkles } from "lucide-react";
import GeometricBackdrop from "./GeometricBackdrop";

const lineReveal = {
    hidden: { y: "115%" },
    show: (i = 0) => ({
        y: "0%",
        transition: { duration: 0.9, delay: 0.15 + i * 0.14, ease: [0.22, 1, 0.36, 1] },
    }),
};

const fadeUp = {
    hidden: { opacity: 0, y: 24 },
    show: (i = 0) => ({
        opacity: 1,
        y: 0,
        transition: { duration: 0.7, delay: 0.5 + i * 0.1, ease: [0.22, 1, 0.36, 1] },
    }),
};

const HERO_LINES = [
    <>We Design Brands</>,
    <>
        That <span className="gold-gradient-text">Get Noticed</span>.
    </>,
];

export const HeroSection = ({ onCtaWork }) => {
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({
        target: ref,
        offset: ["start start", "end start"],
    });
    const backdropY = useTransform(scrollYProgress, [0, 1], ["0%", "28%"]);
    const contentY = useTransform(scrollYProgress, [0, 1], ["0%", "12%"]);

    return (
        <section
            ref={ref}
            data-testid="hero-section"
            className="relative min-h-[92vh] flex items-center pt-28 pb-20 overflow-hidden bg-[#050505]"
        >
            <motion.div style={{ y: backdropY }} className="absolute inset-0">
                <GeometricBackdrop />
            </motion.div>

            <motion.div
                style={{ y: contentY }}
                className="relative max-w-7xl mx-auto px-6 md:px-12 w-full"
            >
                <div className="max-w-4xl">
                    <motion.div
                        initial="hidden"
                        animate="show"
                        custom={0}
                        variants={fadeUp}
                        className="inline-flex items-center gap-2 rounded-full border border-gold/30 bg-gold/5 px-4 py-1.5 mb-8"
                        data-testid="hero-eyebrow"
                    >
                        <Sparkles size={14} className="text-gold" />
                        <span className="font-inter text-xs uppercase tracking-[0.25em] text-gold">
                            Design · Branding · Printing · Nagpur
                        </span>
                    </motion.div>

                    <h1
                        className="font-poppins font-bold tracking-tight text-white text-4xl sm:text-5xl lg:text-7xl leading-[1.08]"
                        data-testid="hero-headline"
                    >
                        {HERO_LINES.map((line, i) => (
                            <span key={i} className="block overflow-hidden pb-[0.12em] -mb-[0.12em]">
                                <motion.span
                                    className="block will-change-transform"
                                    initial="hidden"
                                    animate="show"
                                    custom={i}
                                    variants={lineReveal}
                                >
                                    {line}
                                </motion.span>
                            </span>
                        ))}
                    </h1>

                    <motion.p
                        initial="hidden"
                        animate="show"
                        custom={1}
                        variants={fadeUp}
                        className="mt-6 max-w-2xl font-inter text-base sm:text-lg leading-relaxed text-zinc-400"
                        data-testid="hero-subheadline"
                    >
                        Branding, graphic design, printing, packaging and signage — under one
                        roof. One partner for every visual touchpoint of your business.
                    </motion.p>

                    <motion.div
                        initial="hidden"
                        animate="show"
                        custom={2}
                        variants={fadeUp}
                        className="mt-10 flex flex-col sm:flex-row gap-4"
                    >
                        <Link
                            to="/get-quote"
                            data-testid="hero-cta-quote"
                            className="group inline-flex items-center justify-center gap-2 bg-gold hover:bg-[#C5A030] text-black font-medium font-inter px-8 py-3.5 rounded-full transition-all duration-200 hover:shadow-[0_10px_40px_rgba(212,175,55,0.35)]"
                        >
                            Get a Quote
                            <ArrowRight
                                size={18}
                                className="transition-transform group-hover:translate-x-1"
                            />
                        </Link>
                        <button
                            type="button"
                            onClick={onCtaWork}
                            data-testid="hero-cta-work"
                            className="group inline-flex items-center justify-center gap-2 border border-white/20 text-white hover:border-gold hover:text-gold font-medium font-inter px-8 py-3.5 rounded-full transition-all duration-200"
                        >
                            View Our Work
                            <ArrowRight
                                size={18}
                                className="transition-transform group-hover:translate-x-1"
                            />
                        </button>
                    </motion.div>

                    <motion.div
                        initial="hidden"
                        animate="show"
                        custom={3}
                        variants={fadeUp}
                        className="mt-16 flex flex-wrap gap-x-10 gap-y-4 items-center"
                        data-testid="hero-stats"
                    >
                        {[
                            { k: "200+", v: "Brands Crafted" },
                            { k: "8+ yrs", v: "Studio Experience" },
                            { k: "100%", v: "Hands‑on Delivery" },
                        ].map((s) => (
                            <div key={s.v} className="flex items-baseline gap-3">
                                <div className="font-poppins font-semibold text-white text-2xl">
                                    {s.k}
                                </div>
                                <div className="font-inter text-xs uppercase tracking-[0.2em] text-zinc-500">
                                    {s.v}
                                </div>
                            </div>
                        ))}
                    </motion.div>
                </div>
            </motion.div>

            <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 text-zinc-500">
                <span className="font-inter text-[10px] uppercase tracking-[0.3em]">Scroll</span>
                <span className="w-px h-10 bg-gradient-to-b from-gold to-transparent" />
            </div>
        </section>
    );
};

export default HeroSection;
